import { api, setCsrfToken } from "./client";
import type {
  AdminSession,
  AuthSuccessResponse,
  LoginChallengeResponse,
  LoginRequest,
  MeResponse,
  RecoveryCodeVerifyRequest,
  TotpConfirmRequest,
  TotpConfirmResponse,
  TotpEnrollResponse,
  TotpVerifyRequest,
} from "./types";

/** docs/API_CONTRACT.md - POST /api/auth/login */
export function login(payload: LoginRequest): Promise<LoginChallengeResponse> {
  return api.post<LoginChallengeResponse>("/auth/login", payload, {
    suppressUnauthorizedBroadcast: true,
  });
}

/** docs/API_CONTRACT.md - POST /api/auth/totp/verify */
export async function verifyTotp(
  payload: TotpVerifyRequest,
): Promise<AuthSuccessResponse> {
  const result = await api.post<AuthSuccessResponse>("/auth/totp/verify", payload, {
    suppressUnauthorizedBroadcast: true,
  });
  setCsrfToken(result.csrfToken);
  return result;
}

/** docs/API_CONTRACT.md - POST /api/auth/recovery-code/verify */
export async function verifyRecoveryCode(
  payload: RecoveryCodeVerifyRequest,
): Promise<AuthSuccessResponse> {
  const result = await api.post<AuthSuccessResponse>(
    "/auth/recovery-code/verify",
    payload,
    { suppressUnauthorizedBroadcast: true },
  );
  setCsrfToken(result.csrfToken);
  return result;
}

/**
 * docs/API_CONTRACT.md - GET /api/auth/me
 *
 * Also used on app boot to recover the authenticated state after a page
 * reload. If the backend opportunistically includes a fresh CSRF token here,
 * we adopt it so mutations keep working without forcing a new login.
 */
export async function getMe(): Promise<MeResponse> {
  const result = await api.get<MeResponse & { csrfToken?: string }>("/auth/me", {
    suppressUnauthorizedBroadcast: true,
  });
  if (result.csrfToken) {
    setCsrfToken(result.csrfToken);
  }
  return result;
}

/** docs/API_CONTRACT.md - POST /api/auth/logout */
export async function logout(): Promise<void> {
  try {
    await api.post<void>("/auth/logout", undefined, {
      suppressUnauthorizedBroadcast: true,
    });
  } finally {
    setCsrfToken(null);
  }
}

/** docs/API_CONTRACT.md - GET /api/auth/sessions */
export function listSessions(): Promise<AdminSession[]> {
  return api.get<AdminSession[]>("/auth/sessions");
}

/** docs/API_CONTRACT.md - POST /api/auth/sessions/revoke-all */
export function revokeAllOtherSessions(): Promise<void> {
  return api.post<void>("/auth/sessions/revoke-all");
}

/**
 * docs/API_CONTRACT.md - POST /api/auth/totp/enroll
 *
 * For first enrollment this is called from the password-verified,
 * enrollment-limited challenge state. For rotation it is called from a full
 * authenticated session.
 */
export function enrollTotp(challengeId?: string): Promise<TotpEnrollResponse> {
  return api.post<TotpEnrollResponse>(
    "/auth/totp/enroll",
    challengeId ? { challengeId } : undefined,
    { suppressUnauthorizedBroadcast: true },
  );
}

/** docs/API_CONTRACT.md - POST /api/auth/totp/confirm */
export function confirmTotp(
  payload: TotpConfirmRequest,
): Promise<TotpConfirmResponse> {
  return api
    .post<TotpConfirmResponse>("/auth/totp/confirm", payload, {
    suppressUnauthorizedBroadcast: true,
    })
    .then((result) => {
      if (result.csrfToken) {
        setCsrfToken(result.csrfToken);
      }
      return result;
    });
}
